import type { PublicBrandDto, Theme, WhiteLabel } from "@imagora/contracts";
import { useLayoutEffect, useMemo, useState } from "react";
import type { Session } from "./session";
import { applyTheme, resolveTheme } from "./theme";
import { useMediaQuery } from "./use-media-query";

/** Later wins, token by token, so a host changing one color keeps the rest. */
export function mergeThemes(...themes: ReadonlyArray<Theme | null | undefined>): Theme {
  const merged: Theme = {};
  for (const theme of themes) {
    if (theme?.mode) merged.mode = theme.mode;
    if (theme?.tokens) merged.tokens = { ...merged.tokens, ...theme.tokens };
  }
  return merged;
}

/** What a page without a session knows of the host it opened on. */
function publicWhiteLabel(brand: PublicBrandDto | null | undefined): WhiteLabel | null {
  if (!brand?.whiteLabeled) return null;
  return { name: brand.name, ...(brand.logoUrl ? { logoUrl: brand.logoUrl } : {}), ...(brand.theme ? { theme: brand.theme } : {}) };
}

/** Keeps the document's variables in step with the session, what the host sent since, and the system's preference. */
export function useTheme(session: Session | null, hint: Theme | undefined, publicBrand?: PublicBrandDto | null) {
  const [live, setLive] = useState<Theme | null>(null);
  const systemDark = useMediaQuery("(prefers-color-scheme: dark)");
  const resolved = useMemo(() => resolveTheme(mergeThemes(session ? session.theme : hint, live), session ? session.whiteLabel : publicWhiteLabel(publicBrand)), [session, hint, live, publicBrand]);
  const mode = resolved.mode === "system" ? (systemDark ? "dark" : "light") : resolved.mode;

  useLayoutEffect(() => applyTheme(document.documentElement, resolved, mode), [resolved, mode]);

  return { resolved, setLive: (theme: Theme) => setLive((current) => mergeThemes(current, theme)) };
}
